import React, { useEffect, useState } from "react";
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getItemChart } from "../../../services/cleaning-data-service";
import type { ChartResponse } from "../../../types/api-types";
import LinkChartGraph from "./link-chart-graph";

interface LinkChartDrawerProps {
  open: boolean;
  valueText: string;
  fieldId: number | undefined;
  onClose: () => void;
}

const LinkChartDrawer: React.FC<LinkChartDrawerProps> = ({
  open,
  valueText,
  fieldId,
  onClose,
}) => {
  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !fieldId || !valueText) return undefined;

    // Charts can take minutes to build. Aborting on close or value change stops
    // abandoned requests piling up on the API, which cancels its SQL when the
    // client disconnects, and stops a late response overwriting a newer chart.
    const controller = new AbortController();

    const fetchChartData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getItemChart(
          { fieldId, value: valueText },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setChartData(data);
      } catch (err) {
        if (controller.signal.aborted) return;

        const errorMessage =
          err instanceof Error ? err.message : "Failed to load chart data";
        setError(`${errorMessage}. Please try again.`);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchChartData();

    return () => controller.abort();
  }, [open, fieldId, valueText]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    if (!chartData || chartData.nodes.length === 0) {
      return (
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 400,
          }}
        >
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      );
    }

    return <LinkChartGraph nodes={chartData.nodes} edges={chartData.edges} />;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "90%", md: 900, lg: 1100 },
          maxWidth: "100%",
          mt: 8,
          height: "calc(100vh - 64px)",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Link Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {valueText}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="close"
            sx={{
              color: "text.secondary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Drawer>
  );
};

export default LinkChartDrawer;
